import { confirmDialog, guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { CentEuroFieldMiddleware } from '@adornis/formfield/field-middlewares/CentEuroFieldMiddleware.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { NumberInput } from '@adornis/formfield/fields/number-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import { useForm } from '@adornis/forms/use-form.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { hoverTooltip } from '@adornis/popover/hover-tooltip.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { openValidationErrorDialog } from '@martinus/validation/sg-validation-error-dialog.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html } from 'lit';
import { SgFormGridEntry } from '../../client/components/SgFormGridEntry.js';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { Group } from '../../groups/entities/Group.js';
import { BillingPositionTemplateAPI } from '../api/BillingPositionTemplateAPI.js';
import { BillingRhythm, translateBillingRhythm } from '../entities/BillingPosition.js';
import { BillingPositionTemplate } from '../entities/BillingPositionTemplate.js';

const SGBillingPositionTemplateEditDialogContent = stateful(
  (props: {
    billingPositionTemplate: BillingPositionTemplate | null;
    prefilledScopes?: string[];
    resolve: (billingPosition: BillingPositionTemplate | null) => void;
    reject: () => void;
  }) => {
    const [oldName] = useState(props.billingPositionTemplate?.name, [props.billingPositionTemplate]);
    const [oldKuerzel] = useState(props.billingPositionTemplate?.kuerzel, [props.billingPositionTemplate]);

    const [billingPositionTemplateValue] = useState(
      props.billingPositionTemplate ??
        new BillingPositionTemplate({
          scope: props.prefilledScopes?.[0],
        }),
      [props.billingPositionTemplate],
    );

    const form = useForm(billingPositionTemplateValue, {
      validator: {
        name: sgNonOptional(),
        kuerzel: sgNonOptional(),
      },
    });

    const handleSave = async () => {
      const formErrors = await form.validate();
      if (formErrors) {
        openValidationErrorDialog({
          error: formErrors,
          hardValidate: true,
        });
        return;
      }

      await guardDialog(
        async () => {
          if (form.document._id) {
            await form.document.save();

            /**
             * If the instance is used we need to update all using instances
             */
            const count = await BillingPositionTemplateAPI.countForBillingPosition(
              form.document._id,
              oldName,
              oldKuerzel,
            )();
            console.log('count', count);
            if (count > 0) {
              // at the moment we want to change all corresponding references as the system is no stable. Later a renaming should not affact older mandates
              const userWantsToChange = await confirmDialog(
                `Dieses Template wird in ${count} verschiedenen Gruppen, Buchungen und SEPA-Mandaten verwendet. Wollen Sie eventuelle Namens- und Kürzeländerungen ebenfalls in den Referenzen aktualisieren?`,
                {
                  acceptText: 'Ja, aktualisieren',
                  declineText: 'Nein, nur das Template speichern',
                },
              );
              if (userWantsToChange) {
                await BillingPositionTemplateAPI.renameSepaAndBillingPosition(form.document._id, oldName, oldKuerzel)();
              }
            }
          } else {
            await form.document.create();
          }
        },
        {
          success: () => {
            void SgSnackbar.show('Abrechnungspositions-Template gespeichert.');
          },
        },
      );
    };

    const handleDeletion = async () => {
      await guardDialog(
        async () => {
          await form.document.remove();
        },
        {
          success: () => {
            void SgSnackbar.show('AP-Template gelöscht.');
            props.resolve(null);
          },
        },
      );
    };

    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: `AP-Template ${form.document?._id ? 'bearbeiten' : 'erstellen'}`,
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}

        <p class="block text text-base-content">
          Die AP-Templates sind Vorlagen für Abrechnungspositionen. Sie verhindern, dass auf dem Gruppenlevel
          versehentlich doppelte Positionen angelegt werden und können gleichzeitig eventuelle Werte vorausfüllen.
        </p>

        <div class="grid grid-cols-[1fr_4fr] gap-3 items-center">
          ${SgFormGridEntry({
            label: 'Umgebung',
            render: html`
              <div
                data-tip="Die Umgebung bestimmt in welcher Gruppe das Template verwendet werden kann. Dieses Feld leer zu lassen bedeutet, dass das Template überall verwendet werden kann."
                ${hoverTooltip()}
              >
                ${EntityPickerInput({
                  ...form.field('scope'),
                  class: Group,
                  filter: new AdornisFilter({
                    searchFields: ['name'],
                  }),
                  placeholder: 'Leer = Global',
                  placeholderMode: 'hide',
                  selectionSet: { _id: 1, name: 1 },
                })}
              </div>
            `,
          })}
          ${SgFormGridEntry({
            label: 'Kürzel*',
            render: html`
              ${TextInput({
                ...form.field('kuerzel'),
                placeholder: 'Kein Kürzel angegeben',
                placeholderMode: 'hide',
              })}
            `,
          })}
          ${SgFormGridEntry({
            label: 'Name*',
            render: html`
              ${TextInput({
                ...form.field('name'),
                placeholder: 'Kein Name angegeben',
                placeholderMode: 'hide',
              })}
            `,
          })}
          ${SgFormGridEntry({
            label: 'Preis',
            render: html`
              ${NumberInput({
                ...CentEuroFieldMiddleware(form.field('priceInCents')),
                placeholder: 'Kein Preis angegeben',
                placeholderMode: 'hide',
                suffix: '€',
              })}
            `,
          })}
          ${SgFormGridEntry({
            label: 'Rhythmus',
            render: html`
              ${DropdownSelectionInput({
                ...form.field('rhythm'),
                placeholder: 'Kein Rhythmus angegeben',
                placeholderMode: 'hide',
                clearable: true,
                selectables: Object.values(BillingRhythm),
                renderString: rhythm => translateBillingRhythm(rhythm),
              })}
            `,
          })}
        </div>

        <div class="flex gap-2 justify-end">
          ${
            form.document._id
              ? html`<button class="btn btn-outline btn-error btn-lg" @click=${handleDeletion}>Löschen</button>`
              : ''
          }
          <button class="btn btn-primary btn-lg" @click=${handleSave}>Speichern</button>
        </div>
      </div>
    `;
  },
);

export const BillingPositionTemplateEditDialog = async (
  billingPositionTemplate: BillingPositionTemplate | null,
  prefilledScopes?: string[],
) => {
  await showDialog({
    content: (resolve, reject) =>
      SGBillingPositionTemplateEditDialogContent({
        billingPositionTemplate,
        prefilledScopes,
        resolve,
        reject,
      }),

    modal: true,
  });
};
